import { useTranslation } from "react-i18next";
import { FC, useEffect, useState } from "react";
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

// Helpers
import showToast from "@/_lib/toast";
import { capitalizeWords } from "@/_helpers/text_functions";

// Services and interfaces
import { ApiError } from "@/interfaces/api/error";
import { recipeSchema } from "@/interfaces/recipe/recipe_builder";
import { getFormData, IRecipe } from "@/interfaces/recipe/recipe";
import { useGetRecipeQuery, useUpdateRecipeMutation } from "@/repositories/recipe";

// Styles
import { Edit, Save } from "@carbon/icons-react";

// Components
import { Box } from "@mui/material";
import RecipeModal from './recipe_modal';
import SecondaryToolbar from "@/components/secondary_toolbar";
import RecipeBuilder from "@/components/recipe_builder/recipe_builder";
import UnsavedChangesModal from "@/pages/common/unsaved_changes_modal";
import StatusToggle from "./toggle_published";


const EditRecipePage: FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { id } = useParams();
    const { data: recipe, isLoading: isLoading } = useGetRecipeQuery(String(id));
    
    const [open, setOpen] = useState<string|null>(null);
    
    const [saveRecipe, { isLoading: isSaving }] = useUpdateRecipeMutation();
    const [saveDraftRecipe, { isLoading: isSavingDraft }] = useUpdateRecipeMutation();

    const formMethods = useForm<IRecipe>({
        resolver: zodResolver(recipeSchema),
        mode: 'onBlur',
        defaultValues: {
            title: '',
            image: null,
            description: '',
            servings: 0,
            prep_time: 0,
            cook_time: 0,
            categories: [],
            allergens: [],
            ingredients: [],
            instructions: [],
            calories: 0,
            protein: 0,
            carbs: 0,
            fat: 0,
            fibre: 0,
            alcohol: 0,
        }
    });

    const title = formMethods.watch('title');

    useEffect(() => {
        if (recipe && recipe.id) {
            formMethods.reset({
                title: recipe.title,
                image: recipe.image ?? null,
                description: recipe.description,
                servings: recipe.servings,
                prep_time: recipe.prep_time,
                cook_time: recipe.cook_time,
                categories: recipe.categories,
                diet: recipe.diet,
                allergens: recipe.allergens,
                cuisine: recipe.cuisine,
                ingredients: recipe.ingredients,
                instructions: recipe.instructions,
                calories: recipe.calories,
                protein: recipe.protein,
                carbs: recipe.carbs,
                fat: recipe.fat,
                fibre: recipe.fibre,
                alcohol: recipe.alcohol,
            }, { keepDirtyValues: false, keepDirty: false, keepTouched: false });
            formMethods.clearErrors
        }
    }, [recipe]);

    useEffect(() => {
        // Used to ensure the user is prompted when navigating away with unsaved changes
        console.log(formMethods.formState.dirtyFields);
    }, [formMethods.formState.isDirty]);

    const handleUpdateSettings = (data: IRecipe) => {
        formMethods.setValue('title', data.title, { shouldDirty: true });
        setOpen(null);
    }

    const handleBack = () => {
        if (formMethods.formState.isDirty && Object.keys(formMethods.formState.dirtyFields).length > 0) return setOpen('unsaved');
        navigate(`/library/recipes`);
    }

    const handleSave = async (data: IRecipe) => {
        if (!id) return;
        const formData = getFormData(data);
        const imageBlob = await _getImage();
        if (imageBlob) {
            formData.append('image', imageBlob, 'recipe_image.jpg');
        }
        saveRecipe({id: String(id), data: formData}).unwrap().then(() => {
            showToast({
                type: 'success', 
                title: t('notifications.recipe.updated.title'), 
                message: t('notifications.recipe.updated.message', {name: data.title})
            });
            navigate(`/library/recipes`);
        }).catch((error: ApiError) => {
            showToast({type: 'error', apiError: error.type})
        });
    }

    const handleSaveDraft = async () => {
        if (!id) return;
        const data = formMethods.getValues();
        data.draft = true;
        const formData = getFormData(data);
        const imageBlob = await _getImage();
        if (imageBlob) {
            formData.append('image', imageBlob, 'recipe_image.jpg');
        }
        saveDraftRecipe({id: String(id), data: formData}).unwrap().then(() => {
            showToast({
                type: 'success', 
                title: t('notifications.recipe.updated.title'), 
                message: t('notifications.recipe.updated.message', {name: data.title})
            });
            
            navigate(`/library/recipes`);
        }).catch((error: ApiError) => {
            showToast({type: 'error', apiError: error.type})
        });
    }

    const _getImage = async (): Promise<Blob | undefined> => {
        const image = formMethods.getValues('image');
        const baseUrl = import.meta.env.VITE_MEDIA_URL;
        if (!image || image == '' || image.startsWith(baseUrl)) return;
        const response = await fetch(image);
        const blob = await response.blob();
        return blob;
    }

    return (
        <Box display="flex" flexDirection="column" height="calc(100vh - 40px)" sx={{overflow: 'hidden'}}>

            <SecondaryToolbar
                title={capitalizeWords(title)}
                onBack={handleBack}
                titleAction={{  
                    icon: <Edit />,
                    disabled: isSaving || isSavingDraft,
                    onClick: () => setOpen('settings')
                }}
                action1={{
                    label: t('components.buttons.save'),
                    icon: <Save />,
                    loading: isSaving,
                    disabled: isSavingDraft,
                    onClick: formMethods.handleSubmit(handleSave),
                    sx: {minWidth: '115px'}
                }}
                action2={{
                    label: t('components.buttons.saveDraft'),
                    loading: isSavingDraft,
                    disabled: isSaving,
                    onClick: () => handleSaveDraft(),
                }}
                slot={recipe ? <StatusToggle recipe={recipe} /> : null}
                isLoading={isLoading}
                sx={{borderBottom: 'none'}}
                />

            {open === 'settings' && <RecipeModal
                open={open === 'settings'}
                onClose={() => setOpen(null)}
                recipe={formMethods.getValues()}
                onSave={handleUpdateSettings}
                />}

            {open === 'unsaved' && <UnsavedChangesModal 
                open={open === 'unsaved'} 
                onClose={() => setOpen(null)}
                onSubmit={() => navigate(`/library/recipes`)}
                />}

            <FormProvider {...formMethods}>
                <RecipeBuilder isLoading={isLoading} recipeImage={recipe?.image} />
            </FormProvider>
        </Box>
    )
}

export default EditRecipePage;